import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import FormInfo from "./FormInfo";
import { BreadCrumbLayout } from "../../components/common/BreadCrumbLayout";
import AuthService from "../../services/auth-service/AuthService";

import MiniAppService from "../../services/mini-app-service/MiniAppService";
import useForm from "../../hooks/useForm";

import "../../components/form-view/form-view.css";
import image_default from "../../images/sales-overview/no-product-image.jpg";
import Data from "../../data/Data";

import { buildRevisionList } from "../../utils/revision-utils/RevisionUtils";
import { buildEditMiniApp } from "../../utils/edit-miniapp-utils/EditMiniAppUtils";
// import { buildOrganization } from "../../utils/organization-utils/OrganizationUtils";
import FormUtils from "../../utils/form-utils/FormUtils";

import ErrorHandler from "../../utils/ErrorHandler";
import Validate from "../../services/validate-service/Validate";
import AlertMessages from "../../utils/AlertMessages";
import SecurityUtils from "../../components/security-container/SecurityUtils";
import ScopesEnum from "../../utils/enums/ScopesEnum";
import { useLocation, useParams } from "react-router-dom";
import { useMountBreadCrumb } from "../../hooks/useMountBreadCrumb";

const MiniAppInfoScreen = () => {
    const initialMiniApp = {
        status: "",
        visible: false,
        permissions: [],
        description: "",
        privacy: { mode: "" }
    };

    const location = useLocation();
    const params = useParams();
    const { values, setValue, setAllValues } = useForm(initialMiniApp);
    const [isFetching, setIsFetching] = useState(false);
    const [visible, setVisible] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [alertTheme, setAlertTheme] = useState("");
    const [alertArea, setAlertArea] = useState("");
    const [alertMessage, setAlertMessage] = useState("");
    const [hasError, setHasError] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [initialName, setInicialName] = useState("");
    // const [itens, setItens] = useState(initialValues);
    const [hasWriteMiniAppScope, setHasWriteMiniAppScope] = useState(false);
    const [hasEditMiniAppScope, setHasEditMiniAppScope] = useState(false);
    const [hasWriteMiniAppAction, setHasWriteMiniAppAction] = useState(false);
    const [scopes, setScopes] = useState([]);

    async function loadMiniAppFromBackend() {
        setIsFetching(true);
        const dataProps = location.state?.dataProps;
        let miniApp, initialName;
        try {
            if (dataProps && dataProps.miniApp)
                miniApp = await MiniAppService.findMiniAppBySlug(
                    dataProps.miniApp.slug
                );
            else miniApp = await MiniAppService.findMiniAppBySlug(params.slug);

            miniApp.icon = miniApp.icon ? miniApp.icon : image_default;

            let miniAppDeepClone = JSON.parse(JSON.stringify(miniApp));
            if (!miniAppDeepClone.privacy) {
                miniAppDeepClone.privacy = values.privacy;
            }
            // const org = buildOrganization(miniAppDeepClone);
            let miniAppToEdit = buildEditMiniApp(miniAppDeepClone);
            miniAppToEdit.revisions = buildRevisionList(miniAppDeepClone);
            // miniAppToEdit.organization = org;

            initialName = miniAppToEdit.name;

            setAllValues(miniAppToEdit);
            setInicialName(initialName);
            setIsFetching(false);
        } catch (error) {
            setHasError(true);
            ErrorHandler.handleError(error);
        } finally {
            setIsFetching(false);
        }
        if (AuthService.isAdmin()) setIsAdmin(true);
    }

    useEffect(() => {
        loadMiniAppFromBackend();
    }, []);

    useEffect(() => {
        async function getScopesAndActions() {
            const decodedToken = await AuthService.getDecodedToken();
            if (scopes.length === 0) setScopes(decodedToken.scopes);
            const hasPermissionToEdit = scopes.includes(
                ScopesEnum.WRITE_MINIAPP_PROPERTIES
            );
            setHasEditMiniAppScope(hasPermissionToEdit);
            const hasPermission = scopes.includes(ScopesEnum.WRITE_MINAPP);
            setHasWriteMiniAppScope(hasPermission);
            if (!hasPermission) {
                const miniAppActions = await SecurityUtils.userMiniappActions(
                    values.slug
                );
                setHasWriteMiniAppAction(
                    miniAppActions &&
                        miniAppActions.includes(
                            ScopesEnum.WRITE_MINAPP.toUpperCase()
                        )
                );
                return;
            }
            setHasWriteMiniAppAction(true);
        }
        getScopesAndActions();
    }, [values, scopes]);

    const mountBreadCrumb = () => {
        return (
            <BreadCrumbLayout
                initialName={initialName || ""}
                textRoute="Informações do Eitri-App"
            />
        );
    };
    const closeAlert = () => {
        setVisible(false);
    };

    const onLoad = event => {
        const name = event.target.name;
        const value = event.target.src;
        let miniAppDeepClone = JSON.parse(JSON.stringify(values));
        miniAppDeepClone[name] = value ? value : image_default;
        setAllValues(miniAppDeepClone);
    };

    const handleSwitchStatus = () => {
        const miniapp = FormUtils.getNewMiniAppStatus(values);
        setAllValues(miniapp);
    };

    const handleSwitchChange = name => {
        const miniApp = FormUtils.handleSwitchChange(name, values);
        setAllValues(miniApp);
    };

    const editMiniApp = async event => {
        event.preventDefault();
        const EITRI_APP_TYPE = Data.getAppTypes().EITRI_TYPE_ENGINE;
        const miniAppDeepClone = JSON.parse(JSON.stringify(values));
        setIsFetching(isFetching);
        setAlertArea("formSubmit");
        miniAppDeepClone.icon =
            miniAppDeepClone.icon !== image_default
                ? miniAppDeepClone.icon
                : "";
        let theme, statusToPass, errorMessage;
        let invalidInputs = [];
        if (
            Validate.checkNotBasicInputs(miniAppDeepClone, true) &&
            Validate.isValidIcon(miniAppDeepClone.icon) &&
            Validate.checkBasicInputs(miniAppDeepClone)
        ) {
            try {
                if (!values.privacy.mode) {
                    delete miniAppDeepClone.privacy;
                }

                miniAppDeepClone.type = EITRI_APP_TYPE;
                const res = await MiniAppService.updateMiniApp(
                    miniAppDeepClone
                );
                theme = "success";
                statusToPass = res.status;

                await loadMiniAppFromBackend();
            } catch (error) {
                theme = "danger";
                errorMessage = error.message;
                if (error.response) {
                    statusToPass = error.response.status;
                    if (error.response.data) {
                        errorMessage =
                            error.response.data.message || error.message;
                    }
                }
            }
            setAlertMessage(
                statusToPass === 422
                    ? errorMessage
                    : AlertMessages.messageOnSubmitEditedMiniAppInfo(
                          statusToPass,
                          invalidInputs
                      )
            );
        } else {
            theme = "danger";
            statusToPass = "invalid";
            invalidInputs = Validate.getInvalidInputs(miniAppDeepClone, true);
            setAlertMessage(
                AlertMessages.messageOnSubmitEditedMiniAppInfo(
                    statusToPass,
                    invalidInputs
                )
            );
        }

        setIsFetching(false);
        setVisible(true);
        setAlertTheme(theme);

        window.scrollTo(0, 0);
        miniAppDeepClone.icon = miniAppDeepClone.icon
            ? miniAppDeepClone.icon
            : image_default;
    };

    const { setBreadCrumb } = useMountBreadCrumb();

    useEffect(() => {
        setBreadCrumb(!isFetching && mountBreadCrumb());
    }, [initialName, isFetching]);

    return (
        <React.Fragment>
            {hasError ? (
                <span className="info-review--advice-text">
                    Não foi possível carregar o miniApp.
                </span>
            ) : (
                <FormInfo
                    miniApp={values}
                    visible={visible}
                    isAdmin={isAdmin}
                    isFetching={isFetching}
                    alertTheme={alertTheme}
                    alertArea={alertArea}
                    alertMessage={alertMessage}
                    closeAlert={closeAlert}
                    onLoad={onLoad}
                    handleInput={setValue}
                    handleSwitchStatus={handleSwitchStatus}
                    handleSwitchChange={handleSwitchChange}
                    editMiniApp={editMiniApp}
                    hasWriteMiniAppScope={hasWriteMiniAppScope}
                    hasWriteMiniAppAction={hasWriteMiniAppAction}
                    hasEditMiniAppScope={hasEditMiniAppScope}
                />
            )}
        </React.Fragment>
    );
};

MiniAppInfoScreen.propTypes = {
    match: PropTypes.object,
    location: PropTypes.object
};

export default MiniAppInfoScreen;
